import { tr } from '../i18n/tr.ts';

/**
 * Gateway error → a sentence an operator can act on. A known `code` is worded by the app (translated); other specific server messages (409 "already in
 * use", 403 "No access to page", 502 "Meta bind failed: …") are shown; generic framework text and
 * raw 5xx bodies (HTML, stack-ish strings) are replaced with plain words. Pure (node --test).
 */
const GENERIC = new Set([
  'unauthorized',
  'forbidden',
  'forbidden resource',
  'not found',
  'internal server error',
  'something went wrong',
  'bad gateway',
  'bad request',
]);

const PLAIN = {
  401: 'apiError.session',
  403: 'apiError.forbidden',
  404: 'apiError.notFound',
} as const;

/**
 * Gateway error codes the app words itself, in the operator's language. The gateway keeps an English `message` for
 * every error; an unknown code (or none) falls back to it. Add a code here and its text to en.json + mn.json.
 */
export const API_ERROR_CODES = {
  CONVERSATION_NOT_FOUND: 'apiError.code.conversationNotFound',
  PAGE_NOT_FOUND: 'apiError.code.pageNotFound',
  NO_PAGE_ACCESS: 'apiError.code.noPageAccess',
  NO_TENANT_ACCESS: 'apiError.code.noTenantAccess',
  NOT_TENANT_MEMBER: 'apiError.code.notTenantMember',
  TENANT_NOT_FOUND: 'apiError.code.tenantNotFound',
  INSUFFICIENT_ROLE: 'apiError.code.insufficientRole',
  GLOBAL_ADMIN_ONLY: 'apiError.code.globalAdminOnly',
  SHORTCUT_IN_USE: 'apiError.code.shortcutInUse',
  CANNED_NOT_FOUND: 'apiError.code.cannedNotFound',
  NOT_YOUR_CANNED: 'apiError.code.notYourCanned',
  NOTE_NOT_FOUND: 'apiError.code.noteNotFound',
  NOTE_NOT_AUTHOR: 'apiError.code.noteNotAuthor',
  TAG_NOT_FOUND: 'apiError.code.tagNotFound',
  PERSONA_NOT_FOUND: 'apiError.code.personaNotFound',
  PERSONA_ALREADY_EXISTS: 'apiError.code.personaAlreadyExists',
  PAGE_TOKEN_UNAVAILABLE: 'apiError.code.pageTokenUnavailable',
  REOPEN_BLOCKED: 'apiError.code.reopenBlocked',
  V2_NO_AUTOMATION: 'apiError.code.v2NoAutomation',
  V2_DISABLED: 'apiError.code.v2Disabled',
  V2_REJECTED: 'apiError.code.v2Rejected',
  TENANT_SEARCH_UNAVAILABLE: 'apiError.code.tenantSearchUnavailable',
} as const;

interface ServerError {
  message: string | null;
  code: string | null;
  shortcut?: unknown;
}

function serverError(bodyText: string): ServerError {
  const text = bodyText.trim();
  if (!text) return { message: null, code: null };
  try {
    const parsed = JSON.parse(text) as { message?: unknown; code?: unknown; shortcut?: unknown };
    const m = parsed?.message;
    const code = typeof parsed?.code === 'string' ? parsed.code : null;
    const message = Array.isArray(m) ? m.map(String).join('; ') || null : typeof m === 'string' ? m.trim() || null : null;
    return { message, code, shortcut: parsed?.shortcut };
  } catch {
    // Not JSON: plain text is usable, markup is not.
    return { message: text.startsWith('<') ? null : text, code: null };
  }
}

export function apiErrorMessage(action: string, status: number, bodyText: string): string {
  const { message: msg, code, shortcut } = serverError(bodyText);
  if (code && Object.prototype.hasOwnProperty.call(API_ERROR_CODES, code)) {
    const key = API_ERROR_CODES[code as keyof typeof API_ERROR_CODES];
    return `${action}: ${key === API_ERROR_CODES.SHORTCUT_IN_USE ? tr(key, { shortcut: String(shortcut ?? '') }) : tr(key)}`;
  }
  const specific = msg && !GENERIC.has(msg.toLowerCase()) ? msg : null;
  if (status >= 500) {
    // Upstream failures we name on purpose (Meta) are specific; anything else stays generic.
    return `${action}: ${status === 502 && specific ? specific : tr('apiError.serverFault')}`;
  }
  if (specific) return `${action}: ${specific}`;
  if (status in PLAIN) return `${action}: ${tr(PLAIN[status as keyof typeof PLAIN])}`;
  return tr('apiError.failed', { action, status });
}

export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export async function readApiError(res: Response, action: string): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  return new ApiError(apiErrorMessage(action, res.status, text), res.status);
}
